(function () {
  class NotifyVariantSelects extends HTMLElement {
    static get observedAttributes() {
      return ['data-product-selected-option'];
    }

    constructor() {
      super();
      this.variants = [];
      this.footSizeInputs = [];
      this.sizeInputs = [];
      this.selectedFoot = '';
      this.selectedSize = '';

      // bind public methods
      this.applyProductSelectedOption = this.applyProductSelectedOption.bind(this);
      this.getSelected = this.getSelected.bind(this);
      this.handleInputChange = this.handleInputChange.bind(this);
    }

    connectedCallback() {
      // parse variants JSON if present in the element
      const productDataEl = this.querySelector('#notify-product-data');
      if (productDataEl) {
        try {
          this.variants = JSON.parse(productDataEl.textContent || '[]');
        } catch (e) {
          this.variants = [];
        }
      }

      this.refreshInputs();

      // displays
      this.displaySpan = this.querySelector('.form__label__value');
      this.footDisplay = this.querySelector('.form__label__foot-value');
      this.sizeDisplay = this.querySelector('.form__label__size-value');

      // make sure nothing is checked by default per requirement
      this.clearAllChecks();

      // initial availability (no foot selected)
      this.updateSizeAvailability('');

      // handle radio changes with event delegation
      this.addEventListener('change', this.handleInputChange);

      // Expose the API so other scripts can call it directly
      window.applyNotifyProductSelectedOption = (val) => this.applyProductSelectedOption(val);

      // If attribute already present on connect, apply it
      const initial = this.getAttribute('data-product-selected-option') || '';
      if (initial.trim()) this.applyProductSelectedOption(initial);
    }

    handleInputChange(event) {
      const target = event.target;
      if (!target || target.tagName !== 'INPUT' || target.type !== 'radio') return;

      if (target.name === 'Foot Size' && target.checked) {
        this.selectedFoot = target.value;
        this.updateSizeAvailability(this.selectedFoot);
        this.sizeInputs.forEach(i => { i.checked = false; i.removeAttribute('checked'); });
        this.selectedSize = '';
        this.updateDisplay();
        return;
      }

      if (target.name === 'Size' && target.checked) {
        this.selectedSize = target.value;
        target.setAttribute('checked', 'checked');
        this.updateDisplay();
      }
    }

    refreshInputs() {
      this.footSizeInputs = Array.from(this.querySelectorAll('input[name="Foot Size"]'));
      this.sizeInputs = Array.from(this.querySelectorAll('input[name="Size"]'));
    }

    updateVariants(variationsHtml, variantsJson) {
      if (typeof variationsHtml === 'string') {
        const variationsWrapper = this.querySelector('.variations');
        if (variationsWrapper) {
          variationsWrapper.innerHTML = variationsHtml;
        }
      }

      if (typeof variantsJson === 'string') {
        try {
          this.variants = JSON.parse(variantsJson || '[]');
        } catch (e) {
          this.variants = [];
        }
      }

      const productDataEl = this.querySelector('#notify-product-data');
      if (productDataEl && typeof variantsJson === 'string') {
        productDataEl.textContent = variantsJson;
      }

      this.refreshInputs();
      this.clearAllChecks();
      this.updateSizeAvailability('');

      const currentOption = this.getAttribute('data-product-selected-option') || '';
      if (currentOption.trim()) {
        this.applyProductSelectedOption(currentOption);
      }
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (name === 'data-product-selected-option' && oldValue !== newValue) {
        this.applyProductSelectedOption(newValue || '');
      }
    }

    /* ---------- helpers ---------- */
    getVariant(sizeVal, footVal) {
      if (!sizeVal || !footVal) return undefined;
      return this.variants.find(v =>
        (v.option1 || '').toString().toLowerCase() === sizeVal.toString().toLowerCase() &&
        (v.option3 || '').toString().toLowerCase() === footVal.toString().toLowerCase()
      );
    }

    clearAllChecks() {
      this.footSizeInputs.forEach(i => { i.checked = false; i.removeAttribute('checked'); });
      this.sizeInputs.forEach(i => { i.checked = false; i.removeAttribute('checked'); });
      this.selectedFoot = '';
      this.selectedSize = '';
      this.updateDisplay();
    }

    updateSizeAvailability(footVal) {
      this.sizeInputs.forEach((input) => {
        const sizeVal = input.value.trim();
        const label = this.querySelector(`label[for="${input.id}"]`);
        if (!label) return;

        const variant = this.getVariant(sizeVal, footVal);
        // mark notify-me-variant if variant missing, unavailable or inventory 0
        if (!variant || !variant.available || (typeof variant.inventory_quantity === 'number' && variant.inventory_quantity <= 0)) {
          label.classList.add('notify-me-variant');
        } else {
          label.classList.remove('notify-me-variant');
        }
      });
    }

    updateDisplay() {
      if (this.footDisplay) this.footDisplay.textContent = this.selectedFoot || '';
      if (this.sizeDisplay) this.sizeDisplay.textContent = this.selectedSize || 'Please select';
      if (this.displaySpan) {
        this.displaySpan.setAttribute('data-notify-foot', this.selectedFoot || '');
        this.displaySpan.setAttribute('data-notify-size', this.selectedSize || '');
      }
      // update host attribute for downstream usage
      this.setAttribute('data-notify-me-size', `${this.selectedFoot}/${this.selectedSize}`);

      // Dispatch an event so external code (submit logic) can react without MutationObserver
      this.dispatchEvent(new CustomEvent('notify:selectionChanged', {
        bubbles: true,
        composed: true,
        detail: { foot: this.selectedFoot || '', size: this.selectedSize || '' }
      }));
      this.updateNotifySku();
    }
    getSelected() {
      return { foot: this.selectedFoot || '', size: this.selectedSize || '' };
    }

    buildStructuredVariantTitle() {
      const notifySize = this.getAttribute('data-notify-me-size') || '';
      const productOption = this.getAttribute('data-product-selected-option') || '';

      const notifyParts = notifySize.split('/');
      if (notifyParts.length < 2) return '';

      const foot = notifyParts[0].trim();
      const size = notifyParts[1].trim();

      const optionParts = productOption.split(',');
      if (optionParts.length < 2) return '';

      const color = optionParts[1].trim();

      return `${size} / ${color} / ${foot}`;
    }

    updateNotifySku() {
      if (!Array.isArray(this.variants) || !this.variants.length) {
        this.setAttribute('data-notify-sku', 'unavailable');
        return;
      }

      const structuredTitle = this.buildStructuredVariantTitle();

      if (!structuredTitle) {
        this.setAttribute('data-notify-sku', 'unavailable');
        return;
      }

      const matchedVariant = this.variants.find(v =>
        (v.title || '').toLowerCase() === structuredTitle.toLowerCase()
      );

      if (matchedVariant && matchedVariant.sku) {
        this.setAttribute('data-notify-sku', matchedVariant.sku);
      } else {
        this.setAttribute('data-notify-sku', 'unavailable');
      }
    }


    /* ---------- Main API ---------- */
    applyProductSelectedOption(optionString) {
      // If blank -> clear everything and update availability
      if (!optionString || !optionString.trim()) {
        this.clearAllChecks();
        this.updateSizeAvailability('');
        return;
      }

      // Parse: first part is size, last part is foot (per your format)
      const parts = optionString.split(',').map(p => p.trim());
      const sizeVal = parts[0] || '';
      const footVal = parts[parts.length - 1] || '';

      // Clear current checks
      this.footSizeInputs.forEach(i => { i.checked = false; i.removeAttribute('checked'); });
      this.sizeInputs.forEach(i => { i.checked = false; i.removeAttribute('checked'); });

      // 1) Select foot if exists (Regular / Narrow)
      const matchingFootInput = this.footSizeInputs.find(i => (i.value || '').toString().toLowerCase() === footVal.toString().toLowerCase());
      if (matchingFootInput) {
        matchingFootInput.checked = true;
        matchingFootInput.setAttribute('checked', 'checked');
        this.selectedFoot = matchingFootInput.value;
      } else {
        this.selectedFoot = '';
      }

      // 2) Update availability (labels) for that foot
      this.updateSizeAvailability(this.selectedFoot);

      // 3) Try to select size only if its label has notify-me-variant class
      if (sizeVal) {
        const candidateSizeInput = this.sizeInputs.find(i => (i.value || '').toString().toLowerCase() === sizeVal.toString().toLowerCase());
        if (candidateSizeInput) {
          const candidateLabel = this.querySelector(`label[for="${candidateSizeInput.id}"]`);
          if (candidateLabel && candidateLabel.classList.contains('notify-me-variant')) {
            candidateSizeInput.checked = true;
            candidateSizeInput.setAttribute('checked', 'checked');
            this.selectedSize = candidateSizeInput.value;
          } else {
            // don't auto-check if not a notify-me candidate
            this.selectedSize = '';
          }
        } else {
          this.selectedSize = '';
        }
      } else {
        this.selectedSize = '';
      }

      this.updateDisplay();
    }
  }

  // Define the custom element name (must contain a hyphen)
  if (!customElements.get('notify-variant-selects')) {
    customElements.define('notify-variant-selects', NotifyVariantSelects);
  }

  /* ========== Optional runtime replacer (no Liquid change needed) ========== */
  // If your Liquid still renders a <div id="notify-variant-selects">, this snippet
  // replaces it with the custom element (copies attributes + innerHTML).
  function replaceDivWithCustomElementIfNeeded() {
    const old = document.getElementById('notify-variant-selects');
    if (old && old.tagName.toLowerCase() !== 'notify-variant-selects') {
      const wrapper = document.createElement('notify-variant-selects');
      // copy attributes
      Array.from(old.attributes).forEach(a => wrapper.setAttribute(a.name, a.value));
      // copy innerHTML
      wrapper.innerHTML = old.innerHTML;
      old.replaceWith(wrapper);
      return wrapper;
    }
    // not replaced; return whatever we find
    return document.getElementById('notify-variant-selects');
  }

})();

class NotifySubmitButton extends HTMLElement {
  constructor() {
    super();
    this.handleClick = this.handleClick.bind(this);
  }

  connectedCallback() {
    const root = this.closest('modal-dialog');

    this.emailInput = root.querySelector('#notify-email');
    this.errorMsg = root.querySelector('#notify-error');
    this.successIconContainer = root.querySelector('#notify-email-submit');
    this.circlePath = root.querySelector('#notify-circle');
    this.checkPath = root.querySelector('#notify-check');
    this.notifyComponent = root.querySelector('notify-variant-selects');

    this.loadingOverlay = this.querySelector('.loading-overlay');
    this.submitText = this.querySelector('.survey-submit-text');

    this.addEventListener('click', this.handleClick);

    this.updateState();

    // listen for changes
    if (this.emailInput) {
      this.emailInput.addEventListener('input', () => this.updateState());
    }

    if (this.notifyComponent) {
      this.notifyComponent.addEventListener('notify:selectionChanged', () => {
        this.updateState();
      });
    }
    // Blur validation (restore old behavior)
    if (this.emailInput) {
      this.emailInput.addEventListener('blur', () => {
        const email = this.emailInput.value.trim();
        if (!this.isValidEmail(email)) {
          this.showError(true);
        }
      });

      this.emailInput.addEventListener('input', () => {
        this.showError(false);

        // reset success animation when user types again
        if (this.successIconContainer) this.successIconContainer.classList.add('hidden');
        if (this.circlePath) this.circlePath.classList.remove('circle-path');
        if (this.checkPath) this.checkPath.classList.remove('check-path');

        this.updateState();
      });
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
  }

  isValidEmail(email) {
    return /^[A-Za-z0-9][A-Za-z0-9._%+-]*@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(email);
  }

  getCosmeticsSku() {
    if (!this.notifyComponent) return '';
    return (this.notifyComponent.getAttribute('data-notify-cosmetics-sku') || '').trim();
  }

  isCosmeticsProduct() {
    return this.getCosmeticsSku() !== '';
  }

  hasCompleteNotifySize() {
    if (!this.notifyComponent) return false;

    const val = this.notifyComponent.getAttribute('data-notify-me-size') || '';
    const parts = val.split('/');
    return parts.length >= 2 && parts[0].trim() && parts[1].trim();
  }

  updateState() {
    if (!this.emailInput) return;

    const emailOk = this.isValidEmail(this.emailInput.value.trim());
    const sizeOk = this.isCosmeticsProduct() || this.hasCompleteNotifySize();

    this.disabled = !(emailOk && sizeOk);
  }

  showError(show) {
    if (!this.errorMsg) return;
    this.errorMsg.classList.toggle('hidden', !show);
  }

  showThankYouPanel() {
    const root = this.closest('modal-dialog');
    if (!root) return;

    const thankWrapper = root.querySelector('.notify-thankyou-message-wrapper');
    if (!thankWrapper) return;

    thankWrapper.classList.add('slide-in');

    const closeButtons = root.querySelectorAll('.notify-thankyou-close');
    closeButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        thankWrapper.classList.remove('slide-in');
      });
    });
  }
  setLoading(isLoading) {
    if (isLoading) {
      this.setAttribute('disabled', 'true');
      this.classList.add('is-loading');

      if (this.loadingOverlay) this.loadingOverlay.style.opacity = '1';
      if (this.submitText) this.submitText.style.opacity = '0';
    } else {
      this.removeAttribute('disabled');
      this.classList.remove('is-loading');

      if (this.loadingOverlay) this.loadingOverlay.style.opacity = '0';
      if (this.submitText) this.submitText.style.opacity = '1';
    }
  }

  async handleClick(e) {
    e.preventDefault();

    if (this.disabled) return;

    const email = this.emailInput.value.trim();

    if (!this.isValidEmail(email)) {
      this.showError(true);
      return;
    }

    const cosmeticsSku = this.getCosmeticsSku();
    const isCosmetics = this.isCosmeticsProduct();

    // Size selection is only required for non-cosmetics products
    if (!isCosmetics && !this.hasCompleteNotifySize()) {
      this.showError(true);
      return;
    }

    const productNotifyVariant = isCosmetics
      ? ''
      : (this.notifyComponent.getAttribute('data-notify-me-size') || '');
    const productUrl = this.notifyComponent.getAttribute('data-url') || '';
    const productId = this.notifyComponent.getAttribute('data-notify-product-id') || '';
    const notifySku = isCosmetics
      ? cosmeticsSku
      : (this.notifyComponent.getAttribute('data-notify-sku') || '');

    // UI loading
    this.setLoading(true);

    try {
      // Google Sheet
      await fetch('https://script.google.com/macros/s/AKfycbwSC4m-g62M50EjQflbj3X9Ki2AARi4yIJvZQmllhBkn1Sl0fiUkYaTqoYM_zx8gURKLQ/exec', {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          productId,
          productUrl,
          notifyVariant: productNotifyVariant,
          notifySku
        })
      });

      // MoEngage
      if (typeof Moengage !== 'undefined') {
        Moengage.add_email(email);
        Moengage.add_unique_user_id(email);
        Moengage.track_event('Notify Me', {
          Email: email,
          'Product ID': productId,
          'Product URL': productUrl,
          'Notify Variant': productNotifyVariant,
          'Notify SKU': notifySku
        });
      }

      this.handleSuccess();

    } catch (err) {
      console.error(err);
      this.setLoading(false);
      this.showError(true);
    }
  }

  handleSuccess() {
    this.setLoading(false);

    this.emailInput.value = '';

    if (this.successIconContainer && this.circlePath && this.checkPath) {
      this.successIconContainer.classList.remove('hidden');
      this.circlePath.classList.add('circle-path');
      this.checkPath.classList.add('check-path');
    }

    this.updateState();

    setTimeout(() => {
      this.showThankYouPanel();
    }, 1500);
  }
}

// define it
if (!customElements.get('notify-submit-button')) {
  customElements.define('notify-submit-button', NotifySubmitButton);
}